import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { useAuth } from '../../../context/AuthContext';
import api, { uploadFile } from '../../../services/api';
import PageHeader from '../../../components/common/PageHeader';
import AlertToast from '../../../components/common/AlertToast';
import './ResumesPageV2.css';

const PLACEMENT_STAGES = [
  { value: 'not_ready', label: 'Not Ready', color: 'secondary' },
  { value: 'ready', label: 'Ready', color: 'primary' },
  { value: 'applied', label: 'Applied', color: 'info' },
  { value: 'interview_scheduled', label: 'Interview', color: 'warning' },
  { value: 'selected', label: 'Selected', color: 'success' },
  { value: 'rejected', label: 'Rejected', color: 'danger' },
  { value: 'placed', label: 'Placed', color: 'success' },
];

const STATUS_META = {
  uploaded: { label: 'Uploaded', color: 'info' },
  updated: { label: 'Updated', color: 'warning' },
  approved: { label: 'Approved', color: 'success' },
  rejected: { label: 'Rejected', color: 'danger' },
};

const MAX_SIZE = 5 * 1024 * 1024;
const DEFAULT_RESUME_TEMPLATES = [
  { key: 'ats', label: 'ATS Standard', accentColor: '#111827' },
  { key: 'classic', label: 'Classic Professional', accentColor: '#2563eb' },
  { key: 'modern', label: 'Modern Sidebar', accentColor: '#7c3aed' },
  { key: 'minimal', label: 'Minimal Clean', accentColor: '#0f766e' },
  { key: 'executive', label: 'Executive Serif', accentColor: '#1d4ed8' },
];
const BUILDER_COLOR_OPTIONS = ['#2563eb', '#7c3aed', '#0f766e', '#ea580c', '#db2777', '#0f172a'];
const RESUME_SECTION_DEFAULTS = {
  summary: 'Professional Summary',
  education: 'Education',
  experience: 'Professional Experience',
  projects: 'Projects',
  internships: 'Internships',
  skills: 'Skills',
  certifications: 'Certifications',
  achievements: 'Achievements',
  languages: 'Languages',
  hobbies: 'Hobbies',
};

const normalizeSectionSettings = (settings = {}) => Object.fromEntries(
  Object.entries(RESUME_SECTION_DEFAULTS).map(([key, fallbackLabel]) => [key, {
    label: String(settings?.[key]?.label || fallbackLabel).trim() || fallbackLabel,
    visible: settings?.[key]?.visible !== false,
  }])
);
const resumeSectionLabel = (profile, key) => normalizeSectionSettings(profile?.sectionSettings)[key]?.label || RESUME_SECTION_DEFAULTS[key];
const isResumeSectionVisible = (profile, key) => normalizeSectionSettings(profile?.sectionSettings)[key]?.visible !== false;

const defaultBuilderProfile = () => ({
  profileTitle: 'Primary Resume',
  resumeType: 'fresher',
  templateKey: 'classic',
  accentColor: '#2563eb',
  contact: {
    fullName: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    country: '',
    headline: '',
    portfolioUrl: '',
    linkedinUrl: '',
    githubUrl: '',
  },
  sectionSettings: normalizeSectionSettings(),
  careerSummary: '',
  education: [{ degree: '', institution: '', boardOrUniversity: '', location: '', startDate: '', endDate: '', score: '', description: '' }],
  experiences: [{ jobTitle: '', companyName: '', location: '', startDate: '', endDate: '', isCurrent: false, summary: '', highlights: [{ value: '' }] }],
  projects: [{ title: '', role: '', organization: '', startDate: '', endDate: '', summary: '', technologies: [{ value: '' }], link: '' }],
  internships: [{ title: '', companyName: '', location: '', startDate: '', endDate: '', summary: '' }],
  technicalSkills: [{ value: '' }],
  certifications: [{ value: '' }],
  achievements: [{ value: '' }],
  languages: [{ value: '' }],
  hobbies: [{ value: '' }],
  autoFillMeta: { sourceCourses: [], sourceCertificates: [], sourceSkills: [], lastAutoFilledAt: null },
  isPrimary: true,
  status: 'draft',
});

const defaultJobForm = () => ({
  title: '',
  companyName: '',
  jobType: 'full_time',
  workMode: 'onsite',
  location: '',
  minSalary: '',
  maxSalary: '',
  imageUrl: '',
  imageFile: null,
  description: '',
  responsibilities: [''],
  requiredSkills: [''],
  preferredCourses: [''],
  preferredCertificates: [''],
  minimumScore: 0,
  applicationDeadline: '',
  applyUrl: '',
  applyEmail: '',
  contactPerson: '',
  isActive: true,
  visibilityMode: 'all_students',
});

const fmtSize = (bytes) => (bytes ? `${(bytes / 1024).toFixed(0)} KB` : '-');
const fmtDate = (value) => (value ? new Date(value).toLocaleDateString('en-IN') : '-');
const fmtSalaryRange = (min, max) => {
  const minValue = Number(min || 0);
  const maxValue = Number(max || 0);
  if (!minValue && !maxValue) return '';
  if (minValue && maxValue) return `Rs ${minValue.toLocaleString('en-IN')} - Rs ${maxValue.toLocaleString('en-IN')}`;
  if (minValue) return `From Rs ${minValue.toLocaleString('en-IN')}`;
  return `Up to Rs ${maxValue.toLocaleString('en-IN')}`;
};

const normalizeTagList = (list = []) =>
  (Array.isArray(list) ? list : [])
    .map((item) => (typeof item === 'string' ? item : item?.value || ''))
    .map((item) => String(item || '').trim())
    .filter(Boolean);

const hasFilledRow = (rows = [], keys = []) => (Array.isArray(rows) ? rows : [])
  .some((row) => keys.some((key) => String(row?.[key] || '').trim()));

const getAtsChecks = (profile = {}) => [
  { label: 'Contact details', passed: Boolean(profile.contact?.fullName && profile.contact?.email && profile.contact?.phone) },
  isResumeSectionVisible(profile, 'summary') ? { label: resumeSectionLabel(profile, 'summary'), passed: String(profile.careerSummary || '').trim().length >= 40 } : null,
  isResumeSectionVisible(profile, 'education') ? { label: resumeSectionLabel(profile, 'education'), passed: hasFilledRow(profile.education, ['degree', 'institution']) } : null,
  isResumeSectionVisible(profile, 'skills') ? { label: resumeSectionLabel(profile, 'skills'), passed: normalizeTagList(profile.technicalSkills).length >= 3 } : null,
  profile.resumeType === 'experienced'
    ? (isResumeSectionVisible(profile, 'experience') ? { label: resumeSectionLabel(profile, 'experience'), passed: hasFilledRow(profile.experiences, ['jobTitle', 'companyName', 'summary']) } : null)
    : ((isResumeSectionVisible(profile, 'projects') || isResumeSectionVisible(profile, 'internships')) ? {
      label: 'Project or internship',
      passed: (isResumeSectionVisible(profile, 'projects') && hasFilledRow(profile.projects, ['title', 'summary']))
        || (isResumeSectionVisible(profile, 'internships') && hasFilledRow(profile.internships, ['title', 'companyName', 'summary'])),
    } : null),
  { label: 'ATS single-column format', passed: profile.templateKey === 'ats' },
].filter(Boolean);

const asciiPdfText = (value) => String(value || '')
  .normalize('NFKD')
  .replace(/[^\x20-\x7E\n]/g, '')
  .replace(/\s+/g, ' ')
  .trim();

const exportAtsPdf = (profile = {}) => {
  const pdf = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 46;
  const contentWidth = pageWidth - (margin * 2);
  let y = margin;

  const ensureSpace = (height = 24) => {
    if (y + height <= pageHeight - margin) return;
    pdf.addPage();
    y = margin;
  };
  const addText = (value, { size = 9.5, bold = false, indent = 0, gap = 4, lineHeight = 1.25 } = {}) => {
    const text = asciiPdfText(value);
    if (!text) return;
    pdf.setFont('helvetica', bold ? 'bold' : 'normal');
    pdf.setFontSize(size);
    const lines = pdf.splitTextToSize(text, contentWidth - indent);
    const height = Math.max(size * lineHeight, lines.length * size * lineHeight);
    ensureSpace(height + gap);
    pdf.text(lines, margin + indent, y);
    y += height + gap;
  };
  const addSection = (title) => {
    ensureSpace(34);
    y += 8;
    addText(title.toUpperCase(), { size: 10, bold: true, gap: 3 });
    pdf.setDrawColor(80, 80, 80);
    pdf.setLineWidth(0.6);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 9;
  };
  const addBullet = (value) => addText(`- ${value}`, { indent: 8, gap: 2 });
  const range = (start, end, current = false) => [start, current ? 'Present' : end].filter(Boolean).join(' - ');
  const contact = profile.contact || {};

  addText(contact.fullName || 'Student Name', { size: 18, bold: true, gap: 4 });
  addText(contact.headline, { size: 10.5, bold: true, gap: 5 });
  addText([
    contact.email,
    contact.phone,
    [contact.city, contact.state, contact.country].filter(Boolean).join(', '),
  ].filter(Boolean).join(' | '), { size: 9, gap: 2 });
  addText([contact.linkedinUrl, contact.githubUrl || contact.portfolioUrl].filter(Boolean).join(' | '), { size: 8.5, gap: 5 });

  if (isResumeSectionVisible(profile, 'summary') && profile.careerSummary) {
    addSection(resumeSectionLabel(profile, 'summary'));
    addText(profile.careerSummary);
  }
  if (isResumeSectionVisible(profile, 'skills') && normalizeTagList(profile.technicalSkills).length) {
    addSection(resumeSectionLabel(profile, 'skills'));
    addText(normalizeTagList(profile.technicalSkills).join(', '));
  }
  if (isResumeSectionVisible(profile, 'experience') && hasFilledRow(profile.experiences, ['jobTitle', 'companyName', 'summary'])) {
    addSection(resumeSectionLabel(profile, 'experience'));
    profile.experiences.filter((item) => item?.jobTitle || item?.companyName || item?.summary).forEach((item) => {
      addText([item.jobTitle, item.companyName].filter(Boolean).join(' | '), { bold: true, gap: 2 });
      addText([item.location, range(item.startDate, item.endDate, item.isCurrent)].filter(Boolean).join(' | '), { size: 8.5, gap: 3 });
      addText(item.summary, { gap: 2 });
      normalizeTagList(item.highlights).forEach(addBullet);
      y += 3;
    });
  }
  if (isResumeSectionVisible(profile, 'projects') && hasFilledRow(profile.projects, ['title', 'summary'])) {
    addSection(resumeSectionLabel(profile, 'projects'));
    profile.projects.filter((item) => item?.title || item?.summary).forEach((item) => {
      addText([item.title, item.role || item.organization].filter(Boolean).join(' | '), { bold: true, gap: 2 });
      addText([range(item.startDate, item.endDate), item.link].filter(Boolean).join(' | '), { size: 8.5, gap: 2 });
      addText(item.summary, { gap: 2 });
      const technologies = normalizeTagList(item.technologies);
      if (technologies.length) addText(`Technologies: ${technologies.join(', ')}`, { size: 8.5, gap: 4 });
    });
  }
  if (isResumeSectionVisible(profile, 'internships') && hasFilledRow(profile.internships, ['title', 'companyName', 'summary'])) {
    addSection(resumeSectionLabel(profile, 'internships'));
    profile.internships.filter((item) => item?.title || item?.companyName || item?.summary).forEach((item) => {
      addText([item.title, item.companyName].filter(Boolean).join(' | '), { bold: true, gap: 2 });
      addText([item.location, range(item.startDate, item.endDate)].filter(Boolean).join(' | '), { size: 8.5, gap: 2 });
      addText(item.summary, { gap: 3 });
    });
  }
  if (isResumeSectionVisible(profile, 'education') && hasFilledRow(profile.education, ['degree', 'institution'])) {
    addSection(resumeSectionLabel(profile, 'education'));
    profile.education.filter((item) => item?.degree || item?.institution).forEach((item) => {
      addText([item.degree, item.institution].filter(Boolean).join(' | '), { bold: true, gap: 2 });
      addText([item.boardOrUniversity, item.location, range(item.startDate, item.endDate), item.score].filter(Boolean).join(' | '), { size: 8.5, gap: 2 });
      addText(item.description, { gap: 3 });
    });
  }
  const certifications = normalizeTagList(profile.certifications);
  if (isResumeSectionVisible(profile, 'certifications') && certifications.length) {
    addSection(resumeSectionLabel(profile, 'certifications'));
    certifications.forEach(addBullet);
  }
  const achievements = normalizeTagList(profile.achievements);
  if (isResumeSectionVisible(profile, 'achievements') && achievements.length) {
    addSection(resumeSectionLabel(profile, 'achievements'));
    achievements.forEach(addBullet);
  }
  const languages = normalizeTagList(profile.languages);
  if (isResumeSectionVisible(profile, 'languages') && languages.length) {
    addSection(resumeSectionLabel(profile, 'languages'));
    addText(languages.join(', '));
  }
  const hobbies = normalizeTagList(profile.hobbies);
  if (isResumeSectionVisible(profile, 'hobbies') && hobbies.length) {
    addSection(resumeSectionLabel(profile, 'hobbies'));
    addText(hobbies.join(', '));
  }

  const fileName = asciiPdfText(contact.fullName || profile.profileTitle || 'resume').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'resume';
  pdf.save(`${fileName}-ats-resume.pdf`);
};

const ensureListObjects = (list = []) => {
  const rows = (Array.isArray(list) ? list : [])
    .map((item) => (typeof item === 'string' ? { value: item } : { value: String(item?.value || '') }));
  return rows;
};

const BSModal = ({ show, title, size = '', onClose, children, footer }) => {
  if (!show) return null;
  return (
    <div className="modal fade show d-block" style={{ background: 'rgba(15, 23, 42, 0.45)', zIndex: 1050 }}>
      <div className={`modal-dialog modal-dialog-scrollable ${size ? `modal-${size}` : ''}`}>
        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: 20 }}>
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">{children}</div>
          {footer ? <div className="modal-footer">{footer}</div> : null}
        </div>
      </div>
    </div>
  );
};

const SectionCard = ({ title, subtitle = '', action = null, className = '', children }) => (
  <div className={`card border-0 shadow-sm h-100 ${className}`.trim()} style={{ borderRadius: 20 }}>
    <div className="card-body p-4">
      <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
        <div>
          <div className="fw-semibold" style={{ fontSize: 18 }}>{title}</div>
          {subtitle ? <div className="text-muted small mt-1">{subtitle}</div> : null}
        </div>
        {action}
      </div>
      {children}
    </div>
  </div>
);

const UploadArea = ({ onUploaded, showAlert }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handlePickedFile = (pickedFile) => {
    if (!pickedFile) return;
    if (pickedFile.size > MAX_SIZE) {
      showAlert('File too large. Max 5 MB.', 'danger');
      return;
    }
    const ext = String(pickedFile.name || '').split('.').pop().toLowerCase();
    if (!['pdf', 'doc', 'docx'].includes(ext)) {
      showAlert('Only PDF, DOC and DOCX are allowed.', 'danger');
      return;
    }
    setFile(pickedFile);
  };

  const uploadResume = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('resume', file);
      await api.post('/training/resumes/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      showAlert('Resume uploaded successfully.');
      setFile(null);
      onUploaded();
    } catch (err) {
      showAlert(err.response?.data?.message || 'Failed to upload resume.', 'danger');
    } finally {
      setUploading(false);
    }
  };

  return (
    <SectionCard className="student-resume-upload-card" title="Upload Existing Resume" subtitle="Add your latest resume and keep it ready for applications.">
      <div
        className={`student-resume-dropzone border text-center ${dragActive ? 'is-dragging' : ''}`}
        style={{ borderStyle: 'dashed', minHeight: 180, cursor: 'pointer' }}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragActive(false);
          handlePickedFile(e.dataTransfer.files?.[0]);
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.doc,.docx"
          className="d-none"
          onChange={(e) => handlePickedFile(e.target.files?.[0])}
        />
        {file ? (
          <>
            <span className="student-resume-upload-icon is-ready"><i className="bi bi-file-earmark-check" /></span>
            <div className="fw-semibold mt-3">{file.name}</div>
            <div className="text-muted small mt-1">{fmtSize(file.size)}</div>
          </>
        ) : (
          <>
            <span className="student-resume-upload-icon"><i className="bi bi-cloud-arrow-up" /></span>
            <div className="fw-semibold mt-3">Drop your resume here</div>
            <div className="text-muted small mt-2">Browse PDF, DOC or DOCX up to 5 MB</div>
          </>
        )}
      </div>
      {file ? (
        <div className="d-flex justify-content-end gap-2 mt-3">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setFile(null)}>Clear</button>
          <button type="button" className="btn btn-primary btn-sm" onClick={uploadResume} disabled={uploading}>
            {uploading ? 'Uploading...' : <><i className="bi bi-cloud-arrow-up me-2" />Upload Resume</>}
          </button>
        </div>
      ) : null}
    </SectionCard>
  );
};

const ResumeCard = ({ doc, isStaff, canApprove, onSetDefault, onDownload, onDelete, onPlacement, onApprove }) => {
  const statusMeta = STATUS_META[doc.status] || { label: doc.status, color: 'secondary' };
  const placementMeta = PLACEMENT_STAGES.find((item) => item.value === doc.placementStatus) || { label: doc.placementStatus || 'Not Ready', color: 'secondary' };
  const studentName = doc.student?.firstName ? `${doc.student.firstName} ${doc.student.lastName || ''}`.trim() : '';

  return (
    <div className={`card border-0 shadow-sm mb-3 student-resume-file-card ${doc.isDefault ? 'is-default' : ''}`} style={{ borderRadius: 18 }}>
      <div className="card-body p-3">
        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
          <div className="d-flex align-items-start gap-3 flex-wrap">
            <div className="student-resume-file-icon d-inline-flex align-items-center justify-content-center">
              <i className={`bi ${doc.fileType === 'pdf' ? 'bi-file-earmark-pdf text-danger' : 'bi-file-earmark-word text-primary'}`} style={{ fontSize: 22 }} />
            </div>
            <div>
              <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                <div className="fw-semibold">{doc.fileName || 'Resume'}</div>
                {doc.isDefault ? <span className="badge rounded-pill text-bg-warning">Default</span> : null}
                <span className={`badge rounded-pill text-bg-${statusMeta.color}`}>{statusMeta.label}</span>
                <span className={`badge rounded-pill text-bg-${placementMeta.color}`}>{placementMeta.label}</span>
              </div>
              {studentName ? <div className="small text-muted">{studentName}</div> : null}
              <div className="small text-muted mt-1">
                {fmtSize(doc.fileSize)} | Uploaded {fmtDate(doc.createdAt)}
                {doc.companyName ? ` | ${doc.companyName}` : ''}
                {doc.salary ? ` | ${doc.salary}` : ''}
              </div>
              {doc.rejectionNote ? <div className="small text-danger mt-2">Note: {doc.rejectionNote}</div> : null}
            </div>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => onDownload(doc)}><i className="bi bi-download me-1" />Download</button>
            {!isStaff && !doc.isDefault ? <button type="button" className="btn btn-outline-warning btn-sm" onClick={() => onSetDefault(doc)}><i className="bi bi-star me-1" />Set Default</button> : null}
            {canApprove && doc.status !== 'approved' ? <button type="button" className="btn btn-outline-success btn-sm" onClick={() => onApprove(doc)}>Approve</button> : null}
            {isStaff ? <button type="button" className="btn btn-outline-info btn-sm" onClick={() => onPlacement(doc)}>Placement</button> : null}
            {!isStaff ? <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => onDelete(doc)} aria-label={`Delete ${doc.fileName || 'resume'}`}><i className="bi bi-trash3" /></button> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const PlacementDialog = ({ show, resume, onClose, onSaved }) => {
  const [form, setForm] = useState({ placementStatus: 'not_ready', companyName: '', interviewDate: '', placementNotes: '', salary: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!resume) return;
    setForm({
      placementStatus: resume.placementStatus || 'not_ready',
      companyName: resume.companyName || '',
      interviewDate: resume.interviewDate ? String(resume.interviewDate).slice(0, 10) : '',
      placementNotes: resume.placementNotes || '',
      salary: resume.salary || '',
    });
  }, [resume]);

  const savePlacement = async () => {
    if (!resume?._id) return;
    setSaving(true);
    try {
      await api.put(`/training/resumes/${resume._id}/placement`, form);
      onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <BSModal
      show={show}
      title="Placement Tracking"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary btn-sm" onClick={savePlacement} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </>
      )}
    >
      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label small">Placement Status</label>
          <select className="form-select form-select-sm" value={form.placementStatus} onChange={(e) => setForm((prev) => ({ ...prev, placementStatus: e.target.value }))}>
            {PLACEMENT_STAGES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        </div>
        <div className="col-md-6">
          <label className="form-label small">Company Name</label>
          <input className="form-control form-control-sm" value={form.companyName} onChange={(e) => setForm((prev) => ({ ...prev, companyName: e.target.value }))} />
        </div>
        <div className="col-md-6">
          <label className="form-label small">Interview Date</label>
          <input type="date" className="form-control form-control-sm" value={form.interviewDate} onChange={(e) => setForm((prev) => ({ ...prev, interviewDate: e.target.value }))} />
        </div>
        <div className="col-md-6">
          <label className="form-label small">Salary / CTC</label>
          <input className="form-control form-control-sm" value={form.salary} onChange={(e) => setForm((prev) => ({ ...prev, salary: e.target.value }))} />
        </div>
        <div className="col-12">
          <label className="form-label small">Placement Notes</label>
          <textarea className="form-control" rows={3} value={form.placementNotes} onChange={(e) => setForm((prev) => ({ ...prev, placementNotes: e.target.value }))} />
        </div>
      </div>
    </BSModal>
  );
};

const StatsBar = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get('/training/resumes/stats/compliance').then((response) => setStats(response.data?.data || null)).catch(() => {});
  }, []);
  if (!stats) return null;
  const tiles = [
    { label: 'Total', value: stats.total || 0, icon: 'bi-files', color: '#2563eb' },
    { label: 'Uploaded', value: stats.uploaded || 0, icon: 'bi-cloud-upload', color: '#0ea5e9' },
    { label: 'Approved', value: stats.approved || 0, icon: 'bi-check-circle', color: '#16a34a' },
    { label: 'Placed', value: stats.placed || 0, icon: 'bi-briefcase', color: '#ea580c' },
  ];
  return (
    <div className="row g-3 mb-3">
      {tiles.map((tile) => (
        <div className="col-6 col-lg-3" key={tile.label}>
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 18 }}>
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="text-muted small">{tile.label}</div>
                  <div className="fw-semibold mt-1" style={{ fontSize: 28 }}>{tile.value}</div>
                </div>
                <div className="rounded-4 d-inline-flex align-items-center justify-content-center" style={{ width: 54, height: 54, background: `${tile.color}15`, color: tile.color }}>
                  <i className={`bi ${tile.icon}`} style={{ fontSize: 20 }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const InlineTextListEditor = ({ label, values, onChange, placeholder }) => {
  const rows = ensureListObjects(values);
  const setRow = (index, value) => {
    const next = rows.map((item, itemIndex) => (itemIndex === index ? { value } : item));
    onChange(next);
  };
  const addRow = () => onChange([...rows, { value: '' }]);
  const removeRow = (index) => onChange(rows.filter((_, itemIndex) => itemIndex !== index));

  return (
    <div className="mb-3">
      <label className="form-label small fw-semibold">{label}</label>
      <div className="d-flex flex-column gap-2">
        {rows.map((row, index) => (
          <div className="input-group input-group-sm" key={`${label}-${index}`}>
            <input className="form-control" value={row.value || ''} placeholder={placeholder} onChange={(e) => setRow(index, e.target.value)} />
            <button type="button" className="btn btn-outline-danger" onClick={() => removeRow(index)}>
              <i className="bi bi-trash" />
            </button>
          </div>
        ))}
      </div>
      <button type="button" className="btn btn-link btn-sm px-0 mt-2" onClick={addRow}>+ Add {label}</button>
    </div>
  );
};

const ExperienceHighlightsEditor = ({ values, onChange }) => {
  const rows = ensureListObjects(values);
  return (
    <div className="d-flex flex-column gap-2">
      {rows.map((row, index) => (
        <div className="input-group input-group-sm" key={`highlight-${index}`}>
          <input className="form-control" value={row.value || ''} placeholder="Achievement or responsibility" onChange={(e) => onChange(rows.map((item, itemIndex) => (itemIndex === index ? { value: e.target.value } : item)))} />
          <button type="button" className="btn btn-outline-danger" onClick={() => onChange(rows.filter((_, itemIndex) => itemIndex !== index))}>
            <i className="bi bi-trash" />
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-link btn-sm px-0" onClick={() => onChange([...rows, { value: '' }])}>+ Add highlight</button>
    </div>
  );
};

const ResumePreview = ({ profile, templates }) => {
  const visualPreviewRef = useRef(null);
  const selectedTemplate = templates.find((item) => item.key === profile.templateKey) || templates[0] || { key: 'classic', label: 'Classic' };
  const skills = normalizeTagList(profile.technicalSkills);
  const certifications = normalizeTagList(profile.certifications);
  const achievements = normalizeTagList(profile.achievements);
  const hasSidebar = ['modern', 'creative'].includes(selectedTemplate.key);
  const atsChecks = getAtsChecks(profile);
  const atsScore = Math.round((atsChecks.filter((item) => item.passed).length / atsChecks.length) * 100);

  const exportVisualTemplate = () => {
    if (!visualPreviewRef.current) return;
    const printWindow = window.open('', '_blank', 'width=900,height=1000');
    if (!printWindow) return;
    printWindow.opener = null;
    const headAssets = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
      .map((node) => node.outerHTML)
      .join('');
    printWindow.document.open();
    printWindow.document.write(`<!doctype html><html><head><title>${selectedTemplate.label} Resume</title>${headAssets}<style>body{margin:0;background:#fff}.student-resume-print{max-width:210mm;margin:0 auto}.student-resume-export-control{display:none!important}@page{size:A4;margin:10mm}@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}.student-resume-print{max-width:none}}</style></head><body><main class="student-resume-print">${visualPreviewRef.current.outerHTML}</main></body></html>`);
    printWindow.document.close();
    printWindow.focus();
    window.setTimeout(() => printWindow.print(), 500);
  };

  if (selectedTemplate.key === 'ats') {
    const contact = profile.contact || {};
    const location = [contact.city, contact.state, contact.country].filter(Boolean).join(', ');
    const contacts = [contact.email, contact.phone, location].filter(Boolean);
    const links = [contact.linkedinUrl, contact.githubUrl || contact.portfolioUrl].filter(Boolean);
    const experiences = (profile.experiences || []).filter((item) => item?.jobTitle || item?.companyName || item?.summary);
    const projects = (profile.projects || []).filter((item) => item?.title || item?.summary);
    const internships = (profile.internships || []).filter((item) => item?.title || item?.companyName || item?.summary);
    const education = (profile.education || []).filter((item) => item?.degree || item?.institution);
    return (
      <div className="card border-0 shadow-sm student-ats-preview-card">
        <div className="student-ats-preview-toolbar">
          <div><small>Live Preview</small><strong>ATS Standard</strong></div>
          <button type="button" onClick={() => exportAtsPdf(profile)}><i className="bi bi-download" />Export ATS PDF</button>
        </div>
        <article className="student-ats-document">
          <header>
            <h1>{contact.fullName || 'Student Name'}</h1>
            {contact.headline && <h2>{contact.headline}</h2>}
            {contacts.length > 0 && <p>{contacts.join(' | ')}</p>}
            {links.length > 0 && <p>{links.join(' | ')}</p>}
          </header>
          {isResumeSectionVisible(profile, 'summary') && profile.careerSummary && <section><h3>{resumeSectionLabel(profile, 'summary')}</h3><p>{profile.careerSummary}</p></section>}
          {isResumeSectionVisible(profile, 'skills') && skills.length > 0 && <section><h3>{resumeSectionLabel(profile, 'skills')}</h3><p>{skills.join(', ')}</p></section>}
          {isResumeSectionVisible(profile, 'experience') && experiences.length > 0 && <section><h3>{resumeSectionLabel(profile, 'experience')}</h3>{experiences.map((item, index) => <div className="student-ats-entry" key={`ats-exp-${index}`}><h4>{[item.jobTitle, item.companyName].filter(Boolean).join(' | ')}</h4><small>{[item.location, [item.startDate, item.isCurrent ? 'Present' : item.endDate].filter(Boolean).join(' - ')].filter(Boolean).join(' | ')}</small>{item.summary && <p>{item.summary}</p>}{normalizeTagList(item.highlights).length > 0 && <ul>{normalizeTagList(item.highlights).map((value) => <li key={value}>{value}</li>)}</ul>}</div>)}</section>}
          {isResumeSectionVisible(profile, 'projects') && projects.length > 0 && <section><h3>{resumeSectionLabel(profile, 'projects')}</h3>{projects.map((item, index) => <div className="student-ats-entry" key={`ats-project-${index}`}><h4>{[item.title, item.role || item.organization].filter(Boolean).join(' | ')}</h4>{item.summary && <p>{item.summary}</p>}{normalizeTagList(item.technologies).length > 0 && <small>Technologies: {normalizeTagList(item.technologies).join(', ')}</small>}</div>)}</section>}
          {isResumeSectionVisible(profile, 'internships') && internships.length > 0 && <section><h3>{resumeSectionLabel(profile, 'internships')}</h3>{internships.map((item, index) => <div className="student-ats-entry" key={`ats-intern-${index}`}><h4>{[item.title, item.companyName].filter(Boolean).join(' | ')}</h4><small>{[item.location, [item.startDate, item.endDate].filter(Boolean).join(' - ')].filter(Boolean).join(' | ')}</small>{item.summary && <p>{item.summary}</p>}</div>)}</section>}
          {isResumeSectionVisible(profile, 'education') && education.length > 0 && <section><h3>{resumeSectionLabel(profile, 'education')}</h3>{education.map((item, index) => <div className="student-ats-entry" key={`ats-edu-${index}`}><h4>{[item.degree, item.institution].filter(Boolean).join(' | ')}</h4><small>{[item.boardOrUniversity, item.location, [item.startDate, item.endDate].filter(Boolean).join(' - '), item.score].filter(Boolean).join(' | ')}</small>{item.description && <p>{item.description}</p>}</div>)}</section>}
          {isResumeSectionVisible(profile, 'certifications') && certifications.length > 0 && <section><h3>{resumeSectionLabel(profile, 'certifications')}</h3><ul>{certifications.map((item) => <li key={item}>{item}</li>)}</ul></section>}
          {isResumeSectionVisible(profile, 'achievements') && achievements.length > 0 && <section><h3>{resumeSectionLabel(profile, 'achievements')}</h3><ul>{achievements.map((item) => <li key={item}>{item}</li>)}</ul></section>}
          {isResumeSectionVisible(profile, 'languages') && normalizeTagList(profile.languages).length > 0 && <section><h3>{resumeSectionLabel(profile, 'languages')}</h3><p>{normalizeTagList(profile.languages).join(', ')}</p></section>}
          {isResumeSectionVisible(profile, 'hobbies') && normalizeTagList(profile.hobbies).length > 0 && <section><h3>{resumeSectionLabel(profile, 'hobbies')}</h3><p>{normalizeTagList(profile.hobbies).join(', ')}</p></section>}
        </article>
        <div className="student-ats-checks">
          <div><strong>ATS format checks</strong><span>{atsScore}% complete</span></div>
          <ul>{atsChecks.map((item) => <li className={item.passed ? 'is-passed' : ''} key={item.label}><i className={`bi ${item.passed ? 'bi-check-circle-fill' : 'bi-circle'}`} />{item.label}</li>)}</ul>
          <small>Formatting improves parser compatibility but cannot guarantee a score from every employer's ATS.</small>
        </div>
      </div>
    );
  }

  return (
    <div ref={visualPreviewRef} className="card border-0 shadow-sm" style={{ borderRadius: 24, overflow: 'hidden' }}>
      <div className="card-body p-0">
        <div className="p-3 border-bottom bg-light d-flex align-items-center justify-content-between gap-3 student-resume-export-control">
          <div>
            <div className="small text-uppercase fw-semibold text-muted">Live Preview</div>
            <div className="fw-semibold mt-1">{selectedTemplate.label}</div>
          </div>
          <button type="button" className="btn btn-primary btn-sm" onClick={exportVisualTemplate}>
            <i className="bi bi-download me-1" />Download PDF
          </button>
        </div>
        <div className="p-4" style={{ background: '#ffffff' }}>
          <div className="row g-0">
            {hasSidebar ? (
              <div className="col-4 pe-3" style={{ borderRight: `3px solid ${profile.accentColor}` }}>
                <div className="small fw-semibold text-uppercase mb-3" style={{ color: profile.accentColor }}>Profile</div>
                <div className="small text-muted mb-2">{profile.contact.email || 'email@example.com'}</div>
                <div className="small text-muted mb-2">{profile.contact.phone || '+91 xxxxx xxxxx'}</div>
                <div className="small text-muted mb-4">{[profile.contact.city, profile.contact.state, profile.contact.country].filter(Boolean).join(', ') || 'City, State'}</div>
                {isResumeSectionVisible(profile, 'skills') && skills.length > 0 && <><div className="small fw-semibold text-uppercase mb-2" style={{ color: profile.accentColor }}>{resumeSectionLabel(profile, 'skills')}</div>
                <div className="d-flex flex-wrap gap-2 mb-4">
                  {skills.slice(0, 8).map((skill) => (
                    <span key={skill} className="badge rounded-pill" style={{ background: `${profile.accentColor}16`, color: profile.accentColor }}>{skill}</span>
                  ))}
                </div></>}
                {isResumeSectionVisible(profile, 'certifications') && certifications.length > 0 && <><div className="small fw-semibold text-uppercase mb-2" style={{ color: profile.accentColor }}>{resumeSectionLabel(profile, 'certifications')}</div>
                <div className="d-flex flex-column gap-2 small text-muted">
                  {certifications.slice(0, 4).map((item) => <div key={item}>{item}</div>)}
                </div></>}
              </div>
            ) : null}
            <div className={hasSidebar ? 'col-8 ps-4' : 'col-12'}>
              <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
                <div>
                  <div className="fw-semibold" style={{ fontSize: 28, lineHeight: 1.1 }}>{profile.contact.fullName || 'Student Name'}</div>
                  <div className="mt-2" style={{ color: profile.accentColor, fontWeight: 600 }}>{profile.contact.headline || 'Career Objective / Headline'}</div>
                </div>
                {!hasSidebar ? (
                  <div className="small text-muted text-end">
                    <div>{profile.contact.email || 'email@example.com'}</div>
                    <div>{profile.contact.phone || '+91 xxxxx xxxxx'}</div>
                    <div>{[profile.contact.city, profile.contact.state].filter(Boolean).join(', ') || 'Location'}</div>
                  </div>
                ) : null}
              </div>

              {isResumeSectionVisible(profile, 'summary') && <div className="mb-4">
                <div className="small fw-semibold text-uppercase mb-2" style={{ color: profile.accentColor }}>{resumeSectionLabel(profile, 'summary')}</div>
                <div className="small text-muted" style={{ lineHeight: 1.7 }}>
                  {profile.careerSummary || 'A concise summary will appear here. It can be auto-filled from completed courses and certificates, then edited by the student.'}
                </div>
              </div>}

              {isResumeSectionVisible(profile, 'education') && profile.education?.length > 0 && <div className="mb-4">
                <div className="small fw-semibold text-uppercase mb-2" style={{ color: profile.accentColor }}>{resumeSectionLabel(profile, 'education')}</div>
                <div className="d-flex flex-column gap-3">
                  {profile.education.slice(0, 2).map((item, index) => (
                    <div key={`edu-preview-${index}`}>
                      <div className="fw-semibold">{item.degree || 'Degree'} {item.score ? `| ${item.score}` : ''}</div>
                      <div className="small text-muted">{item.institution || 'Institution'} {item.boardOrUniversity ? `| ${item.boardOrUniversity}` : ''}</div>
                    </div>
                  ))}
                </div>
              </div>}

              {(isResumeSectionVisible(profile, 'experience') && profile.resumeType === 'experienced' && profile.experiences?.length) ? (
                <div className="mb-4">
                  <div className="small fw-semibold text-uppercase mb-2" style={{ color: profile.accentColor }}>{resumeSectionLabel(profile, 'experience')}</div>
                  {(profile.experiences || []).slice(0, 2).map((item, index) => (
                    <div key={`exp-preview-${index}`} className="mb-3">
                      <div className="fw-semibold">{item.jobTitle || 'Role'} {item.companyName ? `| ${item.companyName}` : ''}</div>
                      <div className="small text-muted">{item.summary || 'Work summary will appear here.'}</div>
                    </div>
                  ))}
                </div>
              ) : null}

              {isResumeSectionVisible(profile, 'projects') && profile.projects?.length > 0 && <div className="mb-4">
                <div className="small fw-semibold text-uppercase mb-2" style={{ color: profile.accentColor }}>{resumeSectionLabel(profile, 'projects')}</div>
                {profile.projects.slice(0, 2).map((item, index) => (
                  <div key={`project-preview-${index}`} className="mb-3">
                    <div className="fw-semibold">{item.title || 'Project Title'}</div>
                    <div className="small text-muted">{item.summary || 'Project summary will appear here.'}</div>
                  </div>
                ))}
              </div>}

              {!hasSidebar ? (
                <>
                  {isResumeSectionVisible(profile, 'skills') && skills.length > 0 && <div className="mb-4">
                    <div className="small fw-semibold text-uppercase mb-2" style={{ color: profile.accentColor }}>{resumeSectionLabel(profile, 'skills')}</div>
                    <div className="d-flex flex-wrap gap-2">
                      {skills.slice(0, 10).map((skill) => (
                        <span key={skill} className="badge rounded-pill" style={{ background: `${profile.accentColor}14`, color: profile.accentColor }}>{skill}</span>
                      ))}
                    </div>
                  </div>}
                  {isResumeSectionVisible(profile, 'certifications') && certifications.length > 0 && <div>
                    <div className="small fw-semibold text-uppercase mb-2" style={{ color: profile.accentColor }}>{resumeSectionLabel(profile, 'certifications')}</div>
                    <div className="small text-muted">
                      {certifications.slice(0, 4).join(' | ')}
                    </div>
                  </div>}
                </>
              ) : null}

              {isResumeSectionVisible(profile, 'achievements') && achievements.length ? (
                <div className="mt-4">
                  <div className="small fw-semibold text-uppercase mb-2" style={{ color: profile.accentColor }}>{resumeSectionLabel(profile, 'achievements')}</div>
                  <ul className="small text-muted mb-0 ps-3">
                    {achievements.slice(0, 4).map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const BuilderSectionEditor = ({ profile, setProfile, builderTemplates, builderSuggestions, onAutoFill, onFormatAts, onSave, saving }) => {
  const updateContact = (key, value) => {
    setProfile((prev) => ({ ...prev, contact: { ...prev.contact, [key]: value } }));
  };

  const updateArrayRow = (field, index, key, value) => {
    setProfile((prev) => ({
      ...prev,
      [field]: (prev[field] || []).map((row, rowIndex) => (rowIndex === index ? { ...row, [key]: value } : row)),
    }));
  };

  const addArrayRow = (field, template) => {
    setProfile((prev) => ({ ...prev, [field]: [...(prev[field] || []), template] }));
  };

  const removeArrayRow = (field, index) => {
    setProfile((prev) => {
      const next = (prev[field] || []).filter((_, rowIndex) => rowIndex !== index);
      return { ...prev, [field]: next };
    });
  };

  const updateSectionSetting = (key, changes) => {
    setProfile((prev) => {
      const settings = normalizeSectionSettings(prev.sectionSettings);
      return {
        ...prev,
        sectionSettings: {
          ...settings,
          [key]: { ...settings[key], ...changes },
        },
      };
    });
  };

  return (
    <div className="row g-4">
      <div className="col-12 col-xl-7">
        <SectionCard
          title="Build Resume"
          subtitle="Choose fresher or experienced flow, auto-fill from completed learning, then edit everything manually."
          action={(
            <div className="d-flex gap-2 flex-wrap">
              <button type="button" className="btn btn-outline-dark btn-sm" onClick={onFormatAts}><i className="bi bi-file-earmark-check me-1" />Format for ATS</button>
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={onAutoFill}>Auto Fill from Learning</button>
              <button type="button" className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save Resume Profile'}</button>
            </div>
          )}
        >
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <label className="form-label small fw-semibold">Resume Type</label>
              <select className="form-select" value={profile.resumeType} onChange={(e) => setProfile((prev) => ({ ...prev, resumeType: e.target.value }))}>
                <option value="fresher">Fresher</option>
                <option value="experienced">Experienced</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label small fw-semibold">Template</label>
              <select className="form-select" value={profile.templateKey} onChange={(e) => setProfile((prev) => ({ ...prev, templateKey: e.target.value }))}>
                {builderTemplates.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label small fw-semibold">Accent Color</label>
              <div className="d-flex gap-2 flex-wrap">
                {BUILDER_COLOR_OPTIONS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`btn p-0 border ${profile.accentColor === color ? 'border-dark border-2' : 'border-light'}`}
                    onClick={() => setProfile((prev) => ({ ...prev, accentColor: color }))}
                    style={{ width: 32, height: 32, borderRadius: 999, background: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="student-resume-section-settings mb-4">
            <div className="student-resume-section-settings-heading">
              <div>
                <strong>Customize Resume Sections</strong>
                <small>Rename a section or hide it from every resume template and PDF.</small>
              </div>
              <button type="button" onClick={() => setProfile((prev) => ({ ...prev, sectionSettings: normalizeSectionSettings() }))}>
                Reset
              </button>
            </div>
            <div className="student-resume-section-settings-grid">
              {Object.entries(normalizeSectionSettings(profile.sectionSettings)).map(([key, setting]) => (
                <div className={`student-resume-section-setting ${setting.visible ? '' : 'is-hidden'}`} key={`resume-section-setting-${key}`}>
                  <span><i className={`bi ${setting.visible ? 'bi-eye' : 'bi-eye-slash'}`} /></span>
                  <input
                    type="text"
                    value={setting.label}
                    maxLength={60}
                    aria-label={`${RESUME_SECTION_DEFAULTS[key]} section name`}
                    onChange={(event) => updateSectionSetting(key, { label: event.target.value })}
                    onBlur={() => {
                      if (!String(setting.label || '').trim()) updateSectionSetting(key, { label: RESUME_SECTION_DEFAULTS[key] });
                    }}
                  />
                  <button
                    type="button"
                    className={setting.visible ? 'is-remove' : 'is-restore'}
                    title={setting.visible ? `Remove ${setting.label} from resume` : `Add ${setting.label} to resume`}
                    onClick={() => updateSectionSetting(key, { visible: !setting.visible })}
                  >
                    <i className={`bi ${setting.visible ? 'bi-trash3' : 'bi-plus-lg'}`} />
                    {setting.visible ? 'Remove' : 'Add'}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label small fw-semibold">Full Name</label>
              <input className="form-control" value={profile.contact.fullName || ''} onChange={(e) => updateContact('fullName', e.target.value)} />
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold">Headline</label>
              <input className="form-control" value={profile.contact.headline || ''} onChange={(e) => updateContact('headline', e.target.value)} placeholder="Frontend Developer | Python Learner | UI Specialist" />
            </div>
            <div className="col-md-4">
              <label className="form-label small fw-semibold">Email</label>
              <input className="form-control" value={profile.contact.email || ''} onChange={(e) => updateContact('email', e.target.value)} />
            </div>
            <div className="col-md-4">
              <label className="form-label small fw-semibold">Phone</label>
              <input className="form-control" value={profile.contact.phone || ''} onChange={(e) => updateContact('phone', e.target.value)} />
            </div>
            <div className="col-md-4">
              <label className="form-label small fw-semibold">City</label>
              <input className="form-control" value={profile.contact.city || ''} onChange={(e) => updateContact('city', e.target.value)} />
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold">LinkedIn URL</label>
              <input className="form-control" value={profile.contact.linkedinUrl || ''} onChange={(e) => updateContact('linkedinUrl', e.target.value)} />
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold">GitHub / Portfolio URL</label>
              <input className="form-control" value={profile.contact.githubUrl || ''} onChange={(e) => updateContact('githubUrl', e.target.value)} />
            </div>
            <div className="col-12">
              <label className="form-label small fw-semibold">Career Summary</label>
              <textarea className="form-control" rows={4} value={profile.careerSummary || ''} onChange={(e) => setProfile((prev) => ({ ...prev, careerSummary: e.target.value }))} />
              {builderSuggestions?.suggestedSummary ? <div className="small text-muted mt-2">Suggested: {builderSuggestions.suggestedSummary}</div> : null}
            </div>
          </div>

          <hr className="my-4" />

          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="fw-semibold">Education</div>
            <button type="button" className="btn btn-link btn-sm px-0" onClick={() => addArrayRow('education', { degree: '', institution: '', boardOrUniversity: '', location: '', startDate: '', endDate: '', score: '', description: '' })}>+ Add Education</button>
          </div>
          <div className="d-flex flex-column gap-3 mb-4">
            {(profile.education || []).map((row, index) => (
              <div className="border rounded-4 p-3" key={`education-${index}`}>
                <div className="row g-3">
                  <div className="col-md-6"><label className="form-label small fw-semibold">Degree</label><input className="form-control" value={row.degree || ''} onChange={(e) => updateArrayRow('education', index, 'degree', e.target.value)} /></div>
                  <div className="col-md-6"><label className="form-label small fw-semibold">Institution</label><input className="form-control" value={row.institution || ''} onChange={(e) => updateArrayRow('education', index, 'institution', e.target.value)} /></div>
                  <div className="col-md-4"><label className="form-label small fw-semibold">Board / University</label><input className="form-control" value={row.boardOrUniversity || ''} onChange={(e) => updateArrayRow('education', index, 'boardOrUniversity', e.target.value)} /></div>
                  <div className="col-md-4"><label className="form-label small fw-semibold">Start Date</label><input className="form-control" value={row.startDate || ''} onChange={(e) => updateArrayRow('education', index, 'startDate', e.target.value)} /></div>
                  <div className="col-md-4"><label className="form-label small fw-semibold">End Date / Year</label><input className="form-control" value={row.endDate || ''} onChange={(e) => updateArrayRow('education', index, 'endDate', e.target.value)} /></div>
                  <div className="col-md-6"><label className="form-label small fw-semibold">Score / Percentage</label><input className="form-control" value={row.score || ''} onChange={(e) => updateArrayRow('education', index, 'score', e.target.value)} /></div>
                  <div className="col-md-6"><label className="form-label small fw-semibold">Location</label><input className="form-control" value={row.location || ''} onChange={(e) => updateArrayRow('education', index, 'location', e.target.value)} /></div>
                  <div className="col-12"><label className="form-label small fw-semibold">Description</label><textarea className="form-control" rows={2} value={row.description || ''} onChange={(e) => updateArrayRow('education', index, 'description', e.target.value)} /></div>
                </div>
                <div className="d-flex justify-content-end mt-3">
                  <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeArrayRow('education', index)}>Remove</button>
                </div>
              </div>
            ))}
          </div>

          {profile.resumeType === 'experienced' ? (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="fw-semibold">Experience</div>
                <button type="button" className="btn btn-link btn-sm px-0" onClick={() => addArrayRow('experiences', { jobTitle: '', companyName: '', location: '', startDate: '', endDate: '', isCurrent: false, summary: '', highlights: [{ value: '' }] })}>+ Add Experience</button>
              </div>
              <div className="d-flex flex-column gap-3 mb-4">
                {(profile.experiences || []).map((row, index) => (
                  <div className="border rounded-4 p-3" key={`experience-${index}`}>
                    <div className="row g-3">
                      <div className="col-md-6"><label className="form-label small fw-semibold">Job Title</label><input className="form-control" value={row.jobTitle || ''} onChange={(e) => updateArrayRow('experiences', index, 'jobTitle', e.target.value)} /></div>
                      <div className="col-md-6"><label className="form-label small fw-semibold">Company</label><input className="form-control" value={row.companyName || ''} onChange={(e) => updateArrayRow('experiences', index, 'companyName', e.target.value)} /></div>
                      <div className="col-md-4"><label className="form-label small fw-semibold">Start Date</label><input className="form-control" value={row.startDate || ''} onChange={(e) => updateArrayRow('experiences', index, 'startDate', e.target.value)} /></div>
                      <div className="col-md-4"><label className="form-label small fw-semibold">End Date</label><input className="form-control" value={row.endDate || ''} onChange={(e) => updateArrayRow('experiences', index, 'endDate', e.target.value)} /></div>
                      <div className="col-md-4"><label className="form-label small fw-semibold">Location</label><input className="form-control" value={row.location || ''} onChange={(e) => updateArrayRow('experiences', index, 'location', e.target.value)} /></div>
                      <div className="col-12"><label className="form-label small fw-semibold">Summary</label><textarea className="form-control" rows={2} value={row.summary || ''} onChange={(e) => updateArrayRow('experiences', index, 'summary', e.target.value)} /></div>
                      <div className="col-12">
                        <label className="form-label small fw-semibold">Highlights</label>
                        <ExperienceHighlightsEditor values={row.highlights || []} onChange={(next) => updateArrayRow('experiences', index, 'highlights', next)} />
                      </div>
                    </div>
                    <div className="d-flex justify-content-end mt-3">
                      <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeArrayRow('experiences', index)}>Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : null}

          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="fw-semibold">Projects</div>
            <button type="button" className="btn btn-link btn-sm px-0" onClick={() => addArrayRow('projects', { title: '', role: '', organization: '', startDate: '', endDate: '', summary: '', technologies: [{ value: '' }], link: '' })}>+ Add Project</button>
          </div>
          <div className="d-flex flex-column gap-3 mb-4">
            {(profile.projects || []).map((row, index) => (
              <div className="border rounded-4 p-3" key={`project-${index}`}>
                <div className="row g-3">
                  <div className="col-md-6"><label className="form-label small fw-semibold">Project Title</label><input className="form-control" value={row.title || ''} onChange={(e) => updateArrayRow('projects', index, 'title', e.target.value)} /></div>
                  <div className="col-md-6"><label className="form-label small fw-semibold">Role</label><input className="form-control" value={row.role || ''} onChange={(e) => updateArrayRow('projects', index, 'role', e.target.value)} /></div>
                  <div className="col-md-6"><label className="form-label small fw-semibold">Organization</label><input className="form-control" value={row.organization || ''} onChange={(e) => updateArrayRow('projects', index, 'organization', e.target.value)} /></div>
                  <div className="col-md-3"><label className="form-label small fw-semibold">Start</label><input className="form-control" value={row.startDate || ''} onChange={(e) => updateArrayRow('projects', index, 'startDate', e.target.value)} /></div>
                  <div className="col-md-3"><label className="form-label small fw-semibold">End</label><input className="form-control" value={row.endDate || ''} onChange={(e) => updateArrayRow('projects', index, 'endDate', e.target.value)} /></div>
                  <div className="col-12"><label className="form-label small fw-semibold">Summary</label><textarea className="form-control" rows={2} value={row.summary || ''} onChange={(e) => updateArrayRow('projects', index, 'summary', e.target.value)} /></div>
                  <div className="col-md-8"><InlineTextListEditor label="Technologies" values={row.technologies || []} onChange={(next) => updateArrayRow('projects', index, 'technologies', next)} placeholder="React / Node / Python" /></div>
                  <div className="col-md-4"><label className="form-label small fw-semibold">Project Link</label><input className="form-control" value={row.link || ''} onChange={(e) => updateArrayRow('projects', index, 'link', e.target.value)} /></div>
                </div>
                <div className="d-flex justify-content-end mt-3">
                  <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeArrayRow('projects', index)}>Remove</button>
                </div>
              </div>
            ))}
          </div>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="fw-semibold">Internships</div>
            <button type="button" className="btn btn-link btn-sm px-0" onClick={() => addArrayRow('internships', { title: '', companyName: '', location: '', startDate: '', endDate: '', summary: '' })}>+ Add Internship</button>
          </div>
          <div className="d-flex flex-column gap-3 mb-4">
            {(profile.internships || []).map((row, index) => (
              <div className="border rounded-4 p-3" key={`internship-${index}`}>
                <div className="row g-3">
                  <div className="col-md-6"><label className="form-label small fw-semibold">Internship Title</label><input className="form-control" value={row.title || ''} onChange={(e) => updateArrayRow('internships', index, 'title', e.target.value)} /></div>
                  <div className="col-md-6"><label className="form-label small fw-semibold">Company</label><input className="form-control" value={row.companyName || ''} onChange={(e) => updateArrayRow('internships', index, 'companyName', e.target.value)} /></div>
                  <div className="col-md-4"><label className="form-label small fw-semibold">Start</label><input className="form-control" value={row.startDate || ''} onChange={(e) => updateArrayRow('internships', index, 'startDate', e.target.value)} /></div>
                  <div className="col-md-4"><label className="form-label small fw-semibold">End</label><input className="form-control" value={row.endDate || ''} onChange={(e) => updateArrayRow('internships', index, 'endDate', e.target.value)} /></div>
                  <div className="col-md-4"><label className="form-label small fw-semibold">Location</label><input className="form-control" value={row.location || ''} onChange={(e) => updateArrayRow('internships', index, 'location', e.target.value)} /></div>
                  <div className="col-12"><label className="form-label small fw-semibold">Summary</label><textarea className="form-control" rows={2} value={row.summary || ''} onChange={(e) => updateArrayRow('internships', index, 'summary', e.target.value)} /></div>
                </div>
                <div className="d-flex justify-content-end mt-3">
                  <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeArrayRow('internships', index)}>Remove</button>
                </div>
              </div>
            ))}
          </div>

          <div className="row g-3">
            <div className="col-md-6"><InlineTextListEditor label="Technical Skills" values={profile.technicalSkills || []} onChange={(next) => setProfile((prev) => ({ ...prev, technicalSkills: next }))} placeholder="JavaScript / React / SQL" /></div>
            <div className="col-md-6"><InlineTextListEditor label="Certifications" values={profile.certifications || []} onChange={(next) => setProfile((prev) => ({ ...prev, certifications: next }))} placeholder="Certificate title" /></div>
            <div className="col-md-6"><InlineTextListEditor label="Achievements" values={profile.achievements || []} onChange={(next) => setProfile((prev) => ({ ...prev, achievements: next }))} placeholder="Award or achievement" /></div>
            <div className="col-md-3"><InlineTextListEditor label="Languages" values={profile.languages || []} onChange={(next) => setProfile((prev) => ({ ...prev, languages: next }))} placeholder="English" /></div>
            <div className="col-md-3"><InlineTextListEditor label="Hobbies" values={profile.hobbies || []} onChange={(next) => setProfile((prev) => ({ ...prev, hobbies: next }))} placeholder="Reading" /></div>
          </div>
        </SectionCard>
      </div>
      <div className="col-12 col-xl-5">
        <ResumePreview profile={profile} templates={builderTemplates} />
      </div>
    </div>
  );
};

const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:5030/api').replace(/\/api\/?$/, '');

const withAuthToken = (url) => {
  if (!url) return '';
  const input = String(url || '').trim();
  if (!input || /^(data:|blob:)/i.test(input)) return input;
  const raw = /^https?:/i.test(input) ? input : `${apiBase}${input.startsWith('/') ? '' : '/'}${input}`;
  const token = localStorage.getItem('token');
  if (!token) return raw;
  if (/[?&]t=/.test(raw)) return raw;
  return raw.includes('?') ? `${raw}&t=${encodeURIComponent(token)}` : `${raw}?t=${encodeURIComponent(token)}`;
};

const JobBoard = ({ jobs, isAdmin, filters, setFilters, onCreate, onEdit, onDelete, onToggleActive }) => {
  const filteredJobs = useMemo(() => {
    const search = String(filters.search || '').trim().toLowerCase();
    return jobs.filter((job) => {
      if (filters.status === 'active' && !job.isActive) return false;
      if (filters.status === 'inactive' && job.isActive) return false;
      if (filters.jobType && String(job.jobType || '') !== String(filters.jobType)) return false;
      if (filters.workMode && String(job.workMode || '') !== String(filters.workMode)) return false;
      if (filters.minSalary && Number(job.maxSalary || 0) < Number(filters.minSalary || 0)) return false;
      if (filters.maxSalary && Number(job.minSalary || 0) > Number(filters.maxSalary || 0)) return false;
      if (!search) return true;
      return [job.title, job.companyName, job.location, ...(job.requiredSkills || [])].some((value) => String(value || '').toLowerCase().includes(search));
    });
  }, [filters, jobs]);

  return (
    <SectionCard
      title={isAdmin ? 'Job Opportunities' : 'Job Opportunities for Students'}
      subtitle={isAdmin ? 'Create and manage job postings from admin side. For now all students can view active jobs.' : 'For now every active job is visible to every student. Eligibility filtering can be tightened later.'}
      action={isAdmin ? <button type="button" className="btn btn-primary btn-sm" onClick={onCreate}>Add Job Posting</button> : null}
    >
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <label className="form-label small fw-semibold">Search</label>
          <input className="form-control" value={filters.search} onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))} placeholder="Search title / company / location" />
        </div>
        <div className="col-md-4">
          <label className="form-label small fw-semibold">Job Type</label>
          <select className="form-select" value={filters.jobType} onChange={(e) => setFilters((prev) => ({ ...prev, jobType: e.target.value }))}>
            <option value="">All Job Types</option>
            <option value="full_time">Full Time</option>
            <option value="part_time">Part Time</option>
            <option value="internship">Internship</option>
            <option value="contract">Contract</option>
            <option value="freelance">Freelance</option>
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label small fw-semibold">Status</label>
          <select className="form-select" value={filters.status} onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}>
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label small fw-semibold">Work Mode</label>
          <select className="form-select" value={filters.workMode} onChange={(e) => setFilters((prev) => ({ ...prev, workMode: e.target.value }))}>
            <option value="">All Work Modes</option>
            <option value="onsite">Onsite</option>
            <option value="remote">Remote</option>
            <option value="hybrid">Hybrid</option>
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label small fw-semibold">Minimum Salary</label>
          <input type="number" className="form-control" value={filters.minSalary} onChange={(e) => setFilters((prev) => ({ ...prev, minSalary: e.target.value }))} placeholder="0" />
        </div>
        <div className="col-md-4">
          <label className="form-label small fw-semibold">Maximum Salary</label>
          <input type="number" className="form-control" value={filters.maxSalary} onChange={(e) => setFilters((prev) => ({ ...prev, maxSalary: e.target.value }))} placeholder="50000" />
        </div>
      </div>

      <div className="row g-3">
        {filteredJobs.length ? filteredJobs.map((job) => (
          <div className="col-12 col-xl-6" key={job._id}>
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 18 }}>
              <div className="card-body p-4">
                {job.imageUrl ? (
                  <img src={withAuthToken(job.imageUrl)} alt={job.companyName || job.title} className="w-100 mb-3" style={{ height: 180, objectFit: 'cover', borderRadius: 16 }} />
                ) : null}
                <div className="d-flex justify-content-between gap-3 align-items-start mb-3">
                  <div>
                    <div className="fw-semibold" style={{ fontSize: 20 }}>{job.title}</div>
                    <div className="text-muted small mt-1">{job.companyName} | {job.location || 'Location TBD'}</div>
                  </div>
                  <span className={`badge rounded-pill ${job.isActive ? 'text-bg-success' : 'text-bg-secondary'}`}>{job.isActive ? 'Active' : 'Inactive'}</span>
                </div>
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <span className="badge rounded-pill text-bg-light border">{String(job.jobType || '').replace(/_/g, ' ')}</span>
                  <span className="badge rounded-pill text-bg-light border">{job.workMode}</span>
                  {fmtSalaryRange(job.minSalary, job.maxSalary) ? <span className="badge rounded-pill text-bg-light border">{fmtSalaryRange(job.minSalary, job.maxSalary)}</span> : null}
                </div>
                <div className="small text-muted mb-3" style={{ lineHeight: 1.7 }}>{job.description || 'No description added yet.'}</div>
                {normalizeTagList(job.requiredSkills).length ? (
                  <div className="mb-3">
                    <div className="small fw-semibold mb-2">Required Skills</div>
                    <div className="d-flex flex-wrap gap-2">
                      {normalizeTagList(job.requiredSkills).slice(0, 10).map((skill) => <span key={skill} className="badge rounded-pill text-bg-primary">{skill}</span>)}
                    </div>
                  </div>
                ) : null}
                <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                  <div className="small text-muted">Deadline: {job.applicationDeadline ? fmtDate(job.applicationDeadline) : 'Open'}</div>
                  {isAdmin ? (
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className={`btn btn-sm ${job.isActive ? 'btn-outline-warning' : 'btn-outline-success'}`}
                        onClick={() => onToggleActive(job)}
                      >
                        {job.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => onEdit(job)}>Edit</button>
                      <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => onDelete(job)}>Delete</button>
                    </div>
                  ) : (
                    <div className="d-flex gap-2">
                      {job.applyUrl ? <a className="btn btn-primary btn-sm" href={job.applyUrl} target="_blank" rel="noreferrer">Apply Now</a> : null}
                      {!job.applyUrl && job.applyEmail ? <a className="btn btn-primary btn-sm" href={`mailto:${job.applyEmail}`}>Apply by Email</a> : null}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )) : (
          <div className="col-12">
            <div className="text-center text-muted py-5 border rounded-4">No job opportunities match the current filters.</div>
          </div>
        )}
      </div>
    </SectionCard>
  );
};

const JobEditorModal = ({ show, form, setForm, editingJob, onClose, onSave, saving }) => {
  const updateList = (field, index, value) => {
    setForm((prev) => ({ ...prev, [field]: (prev[field] || []).map((item, itemIndex) => (itemIndex === index ? value : item)) }));
  };
  const addListRow = (field) => setForm((prev) => ({ ...prev, [field]: [...(prev[field] || []), ''] }));
  const removeListRow = (field, index) => setForm((prev) => {
    const next = (prev[field] || []).filter((_, itemIndex) => itemIndex !== index);
    return { ...prev, [field]: next.length ? next : [''] };
  });

  return (
    <BSModal
      show={show}
      title={editingJob?._id ? 'Edit Job Posting' : 'Add Job Posting'}
      size="xl"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save Job'}</button>
        </>
      )}
    >
      <div className="row g-3">
        <div className="col-md-6"><label className="form-label small fw-semibold">Job Title</label><input className="form-control" value={form.title} onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))} /></div>
        <div className="col-md-6"><label className="form-label small fw-semibold">Company Name</label><input className="form-control" value={form.companyName} onChange={(e) => setForm((prev) => ({ ...prev, companyName: e.target.value }))} /></div>
        <div className="col-md-4">
          <label className="form-label small fw-semibold">Job Type</label>
          <select className="form-select" value={form.jobType} onChange={(e) => setForm((prev) => ({ ...prev, jobType: e.target.value }))}>
            <option value="full_time">Full Time</option>
            <option value="part_time">Part Time</option>
            <option value="internship">Internship</option>
            <option value="contract">Contract</option>
            <option value="freelance">Freelance</option>
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label small fw-semibold">Work Mode</label>
          <select className="form-select" value={form.workMode} onChange={(e) => setForm((prev) => ({ ...prev, workMode: e.target.value }))}>
            <option value="onsite">Onsite</option>
            <option value="remote">Remote</option>
            <option value="hybrid">Hybrid</option>
          </select>
        </div>
        <div className="col-md-4"><label className="form-label small fw-semibold">Location</label><input className="form-control" value={form.location} onChange={(e) => setForm((prev) => ({ ...prev, location: e.target.value }))} /></div>
        <div className="col-md-4"><label className="form-label small fw-semibold">Minimum Salary</label><input type="number" className="form-control" value={form.minSalary} onChange={(e) => setForm((prev) => ({ ...prev, minSalary: e.target.value }))} /></div>
        <div className="col-md-4"><label className="form-label small fw-semibold">Maximum Salary</label><input type="number" className="form-control" value={form.maxSalary} onChange={(e) => setForm((prev) => ({ ...prev, maxSalary: e.target.value }))} /></div>
        <div className="col-md-4"><label className="form-label small fw-semibold">Application Deadline</label><input type="date" className="form-control" value={form.applicationDeadline} onChange={(e) => setForm((prev) => ({ ...prev, applicationDeadline: e.target.value }))} /></div>
        <div className="col-md-6">
          <label className="form-label small fw-semibold">Logo / Job Image</label>
          <input
            type="file"
            className="form-control"
            accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
            onChange={(e) => setForm((prev) => ({ ...prev, imageFile: e.target.files?.[0] || null }))}
          />
          {(form.imageFile || form.imageUrl) ? <div className="small text-muted mt-1">{form.imageFile?.name || 'Image uploaded'}</div> : null}
        </div>
        <div className="col-md-6"><label className="form-label small fw-semibold">Apply URL</label><input className="form-control" value={form.applyUrl} onChange={(e) => setForm((prev) => ({ ...prev, applyUrl: e.target.value }))} /></div>
        <div className="col-md-6"><label className="form-label small fw-semibold">Apply Email</label><input className="form-control" value={form.applyEmail} onChange={(e) => setForm((prev) => ({ ...prev, applyEmail: e.target.value }))} /></div>
        <div className="col-12"><label className="form-label small fw-semibold">Description</label><textarea className="form-control" rows={4} value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} /></div>
        {['responsibilities', 'requiredSkills', 'preferredCourses', 'preferredCertificates'].map((field) => (
          <div className="col-md-6" key={field}>
            <label className="form-label small fw-semibold">{field === 'requiredSkills' ? 'Required Skills' : field === 'preferredCourses' ? 'Preferred Courses' : field === 'preferredCertificates' ? 'Preferred Certificates' : 'Responsibilities'}</label>
            <div className="d-flex flex-column gap-2">
              {(form[field] || ['']).map((row, index) => (
                <div className="input-group input-group-sm" key={`${field}-${index}`}>
                  <input className="form-control" value={row || ''} onChange={(e) => updateList(field, index, e.target.value)} />
                  <button type="button" className="btn btn-outline-danger" onClick={() => removeListRow(field, index)}><i className="bi bi-trash" /></button>
                </div>
              ))}
            </div>
            <button type="button" className="btn btn-link btn-sm px-0 mt-2" onClick={() => addListRow(field)}>+ Add</button>
          </div>
        ))}
        <div className="col-md-4"><label className="form-label small fw-semibold">Minimum Score</label><input type="number" className="form-control" value={form.minimumScore} onChange={(e) => setForm((prev) => ({ ...prev, minimumScore: e.target.value }))} /></div>
        <div className="col-md-4">
          <label className="form-label small fw-semibold">Visibility</label>
          <select className="form-select" value={form.visibilityMode} onChange={(e) => setForm((prev) => ({ ...prev, visibilityMode: e.target.value }))}>
            <option value="all_students">All Students</option>
            <option value="restricted">Restricted (future use)</option>
          </select>
        </div>
        <div className="col-md-4 d-flex align-items-end">
          <div className="form-check form-switch">
            <input className="form-check-input" type="checkbox" checked={!!form.isActive} onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))} />
            <label className="form-check-label">Active Job Posting</label>
          </div>
        </div>
      </div>
    </BSModal>
  );
};

export default function ResumesPageV2() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isJobsRoute = /\/job-opportunities\/?$/.test(String(location.pathname || ''));
  const role = user?.roleKey || user?.role || '';
  const isStaff = ['admin', 'manager', 'team_lead', 'faculty'].includes(role);
  const canApprove = ['admin', 'manager'].includes(role);
  const isStudentRole = role === 'student';

  const initialStudentTab = isJobsRoute || String(searchParams.get('tab') || '').toLowerCase() === 'jobs' ? 'jobs' : 'upload';
  const [activeTab, setActiveTab] = useState(isStudentRole ? initialStudentTab : 'all');
  const [alert, setAlert] = useState({ show: false, message: '', type: 'success' });
  const [myDocs, setMyDocs] = useState([]);
  const [allDocs, setAllDocs] = useState([]);
  const [myLoading, setMyLoading] = useState(false);
  const [allLoading, setAllLoading] = useState(false);
  const [allTotal, setAllTotal] = useState(0);
  const [allPage, setAllPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPlace, setFilterPlace] = useState('');
  const [placementDoc, setPlacementDoc] = useState(null);
  const [builderProfile, setBuilderProfile] = useState(defaultBuilderProfile());
  const [builderTemplates, setBuilderTemplates] = useState(DEFAULT_RESUME_TEMPLATES);
  const [builderSuggestions, setBuilderSuggestions] = useState(null);
  const [builderLoading, setBuilderLoading] = useState(false);
  const [builderSaving, setBuilderSaving] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobFilters, setJobFilters] = useState({ search: '', jobType: '', workMode: '', minSalary: '', maxSalary: '', status: '' });
  const [showJobModal, setShowJobModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [jobForm, setJobForm] = useState(defaultJobForm());
  const [jobSaving, setJobSaving] = useState(false);

  const showAlert = useCallback((message, type = 'success') => setAlert({ show: true, message, type }), []);

  const fetchMy = useCallback(async () => {
    setMyLoading(true);
    try {
      const response = await api.get('/training/resumes/my');
      setMyDocs(response.data?.records || response.data?.data || []);
    } finally {
      setMyLoading(false);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setAllLoading(true);
    try {
      const params = { page: allPage, limit: 20 };
      if (filterStatus) params.status = filterStatus;
      if (filterPlace) params.placementStatus = filterPlace;
      const response = await api.get('/training/resumes', { params });
      setAllDocs(response.data?.records || []);
      setAllTotal(response.data?.total || 0);
    } finally {
      setAllLoading(false);
    }
  }, [allPage, filterPlace, filterStatus]);

  const fetchBuilderTemplates = useCallback(async () => {
    const response = await api.get('/training/resumes/builder/templates');
    const payload = response.data?.data;
    const templates = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.templates)
        ? payload.templates
        : [];
    const availableTemplates = templates.length ? templates : DEFAULT_RESUME_TEMPLATES;
    const atsTemplate = DEFAULT_RESUME_TEMPLATES.find((item) => item.key === 'ats');
    setBuilderTemplates([atsTemplate, ...availableTemplates.filter((item) => item.key !== 'ats')]);
  }, []);

  const fetchBuilderProfile = useCallback(async () => {
    if (!isStudentRole) return;
    setBuilderLoading(true);
    try {
      const response = await api.get('/training/resumes/builder/my');
      const nextProfile = { ...defaultBuilderProfile(), ...(response.data?.data || {}) };
      nextProfile.sectionSettings = normalizeSectionSettings(nextProfile.sectionSettings);
      nextProfile.technicalSkills = ensureListObjects(nextProfile.technicalSkills);
      nextProfile.certifications = ensureListObjects(nextProfile.certifications);
      nextProfile.achievements = ensureListObjects(nextProfile.achievements);
      nextProfile.languages = ensureListObjects(nextProfile.languages);
      nextProfile.hobbies = ensureListObjects(nextProfile.hobbies);
      nextProfile.education = Array.isArray(nextProfile.education) ? nextProfile.education : [];
      nextProfile.experiences = Array.isArray(nextProfile.experiences)
        ? nextProfile.experiences.map((row) => ({ ...row, highlights: ensureListObjects(row.highlights) }))
        : [];
      nextProfile.projects = Array.isArray(nextProfile.projects)
        ? nextProfile.projects.map((row) => ({ ...row, technologies: ensureListObjects(row.technologies) }))
        : [];
      nextProfile.internships = Array.isArray(nextProfile.internships) ? nextProfile.internships : [];
      setBuilderProfile(nextProfile);
    } finally {
      setBuilderLoading(false);
    }
  }, [isStudentRole]);

  const fetchBuilderSuggestions = useCallback(async () => {
    if (!isStudentRole) return;
    const response = await api.get('/training/resumes/builder/suggestions/my');
    setBuilderSuggestions(response.data?.data || null);
  }, [isStudentRole]);

  const fetchJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const response = await api.get('/training/resumes/jobs');
      setJobs(response.data?.records || response.data?.data || []);
    } finally {
      setJobsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBuilderTemplates().catch(() => {});
    fetchJobs().catch(() => {});
  }, [fetchBuilderTemplates, fetchJobs]);

  useEffect(() => {
    if (!isStudentRole) return;
    const nextTab = isJobsRoute || String(searchParams.get('tab') || '').toLowerCase() === 'jobs' ? 'jobs' : activeTab;
    if (nextTab !== activeTab) setActiveTab(nextTab);
  }, [activeTab, isJobsRoute, isStudentRole, searchParams]);

  useEffect(() => {
    if (isStaff) {
      if (activeTab === 'all') fetchAll().catch(() => {});
      return;
    }
    if (activeTab === 'my') fetchMy().catch(() => {});
    if (activeTab === 'build') {
      fetchBuilderProfile().catch(() => {});
      fetchBuilderSuggestions().catch(() => {});
    }
  }, [activeTab, fetchAll, fetchBuilderProfile, fetchBuilderSuggestions, fetchMy, isStaff]);

  useEffect(() => {
    if (!isStaff) fetchMy().catch(() => {});
  }, [fetchMy, isStaff]);

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/training/resumes/${doc._id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = doc.fileName || 'resume';
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (_) {
      showAlert('Download failed.', 'danger');
    }
  };

  const handleSetDefault = async (doc) => {
    try {
      await api.put(`/training/resumes/${doc._id}/default`);
      fetchMy();
      showAlert('Default resume updated.');
    } catch (_) {
      showAlert('Failed to set default resume.', 'danger');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.fileName}"?`)) return;
    try {
      await api.delete(`/training/resumes/${doc._id}`);
      fetchMy();
      showAlert('Resume deleted.');
    } catch (_) {
      showAlert('Failed to delete resume.', 'danger');
    }
  };

  const handleApprove = async (doc) => {
    try {
      await api.put(`/training/resumes/${doc._id}/approve`);
      fetchAll();
      showAlert('Resume approved.');
    } catch (_) {
      showAlert('Failed to approve resume.', 'danger');
    }
  };

  const handleAutoFill = () => {
    if (!builderSuggestions) return;
    setBuilderProfile((prev) => ({
      ...prev,
      contact: {
        ...prev.contact,
        fullName: prev.contact.fullName || builderSuggestions.contact?.fullName || '',
        email: prev.contact.email || builderSuggestions.contact?.email || '',
        phone: prev.contact.phone || builderSuggestions.contact?.phone || '',
      },
      careerSummary: prev.careerSummary || builderSuggestions.suggestedSummary || '',
      technicalSkills: ensureListObjects(Array.from(new Set([...normalizeTagList(prev.technicalSkills), ...(builderSuggestions.technicalSkills || [])]))),
      certifications: ensureListObjects(Array.from(new Set([...normalizeTagList(prev.certifications), ...(builderSuggestions.certifications || [])]))),
      autoFillMeta: {
        sourceCourses: builderSuggestions.sourceCourses || [],
        sourceCertificates: builderSuggestions.certifications || [],
        sourceSkills: builderSuggestions.technicalSkills || [],
        lastAutoFilledAt: new Date().toISOString(),
      },
    }));
    showAlert('Resume builder auto-filled from learning data.');
  };

  const handleFormatAts = () => {
    setBuilderProfile((prev) => ({ ...prev, templateKey: 'ats', accentColor: '#111827' }));
    showAlert('ATS Standard format applied. Complete the highlighted checks before exporting.');
  };

  const saveBuilderProfile = async () => {
    setBuilderSaving(true);
    try {
      await api.put('/training/resumes/builder/my', builderProfile);
      showAlert('Resume builder profile saved.');
      fetchBuilderProfile();
    } catch (err) {
      showAlert(err.response?.data?.message || 'Failed to save resume profile.', 'danger');
    } finally {
      setBuilderSaving(false);
    }
  };

  const openCreateJob = () => {
    setEditingJob(null);
    setJobForm(defaultJobForm());
    setShowJobModal(true);
  };

  const openEditJob = (job) => {
    setEditingJob(job);
    setJobForm({
      title: job.title || '',
      companyName: job.companyName || '',
      jobType: job.jobType || 'full_time',
      workMode: job.workMode || 'onsite',
      location: job.location || '',
      minSalary: job.minSalary || '',
      maxSalary: job.maxSalary || '',
      imageUrl: job.imageUrl || '',
      imageFile: null,
      description: job.description || '',
      responsibilities: normalizeTagList(job.responsibilities).length ? normalizeTagList(job.responsibilities) : [''],
      requiredSkills: normalizeTagList(job.requiredSkills).length ? normalizeTagList(job.requiredSkills) : [''],
      preferredCourses: normalizeTagList(job.preferredCourses).length ? normalizeTagList(job.preferredCourses) : [''],
      preferredCertificates: normalizeTagList(job.preferredCertificates).length ? normalizeTagList(job.preferredCertificates) : [''],
      minimumScore: job.minimumScore || 0,
      applicationDeadline: job.applicationDeadline ? String(job.applicationDeadline).slice(0, 10) : '',
      applyUrl: job.applyUrl || '',
      applyEmail: job.applyEmail || '',
      contactPerson: job.contactPerson || '',
      isActive: job.isActive !== false,
      visibilityMode: job.visibilityMode || 'all_students',
    });
    setShowJobModal(true);
  };

  const saveJob = async () => {
    if (!jobForm.title.trim() || !jobForm.companyName.trim()) {
      showAlert('Job title and company name are required.', 'danger');
      return;
    }
    setJobSaving(true);
    try {
      const payload = new FormData();
      payload.append('title', String(jobForm.title || '').trim());
      payload.append('companyName', String(jobForm.companyName || '').trim());
      payload.append('jobType', String(jobForm.jobType || 'full_time'));
      payload.append('workMode', String(jobForm.workMode || 'onsite'));
      payload.append('location', String(jobForm.location || '').trim());
      payload.append('minSalary', String(jobForm.minSalary || '0'));
      payload.append('maxSalary', String(jobForm.maxSalary || '0'));
      payload.append('description', String(jobForm.description || '').trim());
      payload.append('minimumScore', String(jobForm.minimumScore || 0));
      payload.append('applicationDeadline', String(jobForm.applicationDeadline || ''));
      payload.append('applyUrl', String(jobForm.applyUrl || '').trim());
      payload.append('applyEmail', String(jobForm.applyEmail || '').trim());
      payload.append('contactPerson', String(jobForm.contactPerson || '').trim());
      payload.append('visibilityMode', String(jobForm.visibilityMode || 'all_students'));
      payload.append('isActive', jobForm.isActive ? 'true' : 'false');
      payload.append('responsibilities', JSON.stringify(normalizeTagList(jobForm.responsibilities)));
      payload.append('requiredSkills', JSON.stringify(normalizeTagList(jobForm.requiredSkills)));
      payload.append('preferredCourses', JSON.stringify(normalizeTagList(jobForm.preferredCourses)));
      payload.append('preferredCertificates', JSON.stringify(normalizeTagList(jobForm.preferredCertificates)));
      if (jobForm.imageFile) payload.append('image', jobForm.imageFile);
      else if (jobForm.imageUrl) payload.append('imageUrl', jobForm.imageUrl);
      if (editingJob?._id) {
        await api.put(`/training/resumes/jobs/${editingJob._id}`, payload, { headers: { 'Content-Type': 'multipart/form-data' } });
        showAlert('Job posting updated.');
      } else {
        await uploadFile('/training/resumes/jobs', payload);
        showAlert('Job posting created.');
      }
      setShowJobModal(false);
      setEditingJob(null);
      setJobForm(defaultJobForm());
      fetchJobs();
    } catch (err) {
      showAlert(err.response?.data?.message || 'Failed to save job posting.', 'danger');
    } finally {
      setJobSaving(false);
    }
  };

  const deleteJob = async (job) => {
    if (!window.confirm(`Delete job posting "${job.title}"?`)) return;
    try {
      await api.delete(`/training/resumes/jobs/${job._id}`);
      showAlert('Job posting deleted.');
      fetchJobs();
    } catch (err) {
      showAlert(err.response?.data?.message || 'Failed to delete job posting.', 'danger');
    }
  };

  const toggleJobActive = async (job) => {
    try {
      const payload = new FormData();
      payload.append('title', String(job.title || '').trim());
      payload.append('companyName', String(job.companyName || '').trim());
      payload.append('jobType', String(job.jobType || 'full_time'));
      payload.append('workMode', String(job.workMode || 'onsite'));
      payload.append('location', String(job.location || '').trim());
      payload.append('minSalary', String(job.minSalary || 0));
      payload.append('maxSalary', String(job.maxSalary || 0));
      payload.append('description', String(job.description || '').trim());
      payload.append('minimumScore', String(job.minimumScore || 0));
      payload.append('applicationDeadline', job.applicationDeadline ? String(job.applicationDeadline).slice(0, 10) : '');
      payload.append('applyUrl', String(job.applyUrl || '').trim());
      payload.append('applyEmail', String(job.applyEmail || '').trim());
      payload.append('contactPerson', String(job.contactPerson || '').trim());
      payload.append('visibilityMode', String(job.visibilityMode || 'all_students'));
      payload.append('isActive', job.isActive ? 'false' : 'true');
      payload.append('responsibilities', JSON.stringify(normalizeTagList(job.responsibilities)));
      payload.append('requiredSkills', JSON.stringify(normalizeTagList(job.requiredSkills)));
      payload.append('preferredCourses', JSON.stringify(normalizeTagList(job.preferredCourses)));
      payload.append('preferredCertificates', JSON.stringify(normalizeTagList(job.preferredCertificates)));
      if (job.imageUrl) payload.append('imageUrl', String(job.imageUrl));
      await api.put(`/training/resumes/jobs/${job._id}`, payload, { headers: { 'Content-Type': 'multipart/form-data' } });
      showAlert(`Job posting ${job.isActive ? 'deactivated' : 'activated'}.`);
      fetchJobs();
    } catch (err) {
      showAlert(err.response?.data?.message || 'Failed to update job status.', 'danger');
    }
  };

  const docs = activeTab === 'my' ? myDocs : allDocs;
  const loading = activeTab === 'my' ? myLoading : allLoading;
  const studentResumeSummary = useMemo(() => {
    const skills = normalizeTagList(builderProfile.technicalSkills).length;
    const checks = [
      Boolean(builderProfile.contact?.fullName),
      Boolean(builderProfile.contact?.email),
      Boolean(builderProfile.contact?.phone),
      Boolean(String(builderProfile.careerSummary || '').trim()),
      Boolean(builderProfile.education?.some((item) => item?.degree || item?.institution)),
      skills > 0,
      Boolean(builderProfile.projects?.some((item) => item?.title || item?.summary)),
      myDocs.length > 0,
    ];
    return {
      skills,
      readiness: Math.round((checks.filter(Boolean).length / checks.length) * 100),
      approved: myDocs.filter((doc) => doc.status === 'approved').length,
    };
  }, [builderProfile, myDocs]);

  return (
    <div className={`container-fluid py-3 ${isStudentRole ? 'student-resume-page' : ''}`}>
      <AlertToast message={alert.message} type={alert.type} show={alert.show} onClose={() => setAlert((prev) => ({ ...prev, show: false }))} />
      {isStudentRole ? (
        <section className="student-resume-hero">
          <div className="student-resume-hero-copy">
            <span className="student-resume-eyebrow"><i className="bi bi-stars" /> Career toolkit</span>
            <h1>Build a resume that gets you noticed</h1>
            <p>Create a focused profile, upload your latest resume, and stay ready for the right opportunity.</p>
            <div className="student-resume-hero-actions">
              <button type="button" onClick={() => { setActiveTab('upload'); setSearchParams({}); }}><i className="bi bi-cloud-arrow-up" />Upload Resume</button>
              <button type="button" onClick={() => { setActiveTab('build'); setSearchParams({}); }}><i className="bi bi-pencil-square" />Build Resume</button>
            </div>
          </div>
          <div className="student-resume-hero-summary" aria-label="Resume profile summary">
            <div className="student-resume-score" style={{ '--resume-score': `${studentResumeSummary.readiness * 3.6}deg` }}><strong>{studentResumeSummary.readiness}%</strong><small className="student-resume-score-label">Profile ready</small></div>
            <div className="student-resume-summary-items">
              <span><i className="bi bi-file-earmark-check" /><strong>{myDocs.length}</strong><small>Resume files</small></span>
              <span><i className="bi bi-lightning-charge" /><strong>{studentResumeSummary.skills}</strong><small>Skills added</small></span>
              <span><i className="bi bi-patch-check" /><strong>{studentResumeSummary.approved}</strong><small>Approved</small></span>
            </div>
          </div>
        </section>
      ) : <PageHeader title="Resumes & Jobs" icon="bi-file-person" subtitle="Resume uploads, resume builder, placement tracking and job opportunities" />}
      {isStaff ? <StatsBar /> : null}

      <ul className={`nav nav-tabs mb-4 ${isStudentRole ? 'student-resume-tabs' : ''}`}>
        {!isStaff ? <li className="nav-item"><button type="button" className={`nav-link ${activeTab === 'upload' ? 'active' : ''}`} onClick={() => { setActiveTab('upload'); setSearchParams({}); }}><i className="bi bi-cloud-arrow-up" />Upload</button></li> : null}
        {!isStaff ? <li className="nav-item"><button type="button" className={`nav-link ${activeTab === 'build' ? 'active' : ''}`} onClick={() => { setActiveTab('build'); setSearchParams({}); }}><i className="bi bi-pencil-square" />Builder</button></li> : null}
        <li className="nav-item"><button type="button" className={`nav-link ${activeTab === 'my' ? 'active' : ''}`} onClick={() => { setActiveTab('my'); setSearchParams({}); }}>{!isStaff && <i className="bi bi-folder2-open" />}{isStaff ? 'Student Resumes' : 'My Resumes'}</button></li>
        {isStaff ? <li className="nav-item"><button type="button" className={`nav-link ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>All Resumes</button></li> : null}
        {isStaff ? <li className="nav-item"><button type="button" className={`nav-link ${activeTab === 'jobs' ? 'active' : ''}`} onClick={() => setActiveTab('jobs')}>Job Opportunities</button></li> : null}
      </ul>

      {activeTab === 'upload' && !isStaff ? (
        <div className="row g-3 student-resume-upload-layout">
          <div className="col-12 col-xl-7">
            <UploadArea onUploaded={() => { fetchMy(); setActiveTab('my'); }} showAlert={showAlert} />
          </div>
          <div className="col-12 col-xl-5">
            <SectionCard className="student-resume-guide-card" title="Make every application stronger" subtitle="A quick checklist before you apply.">
              <div className="student-resume-guide-list">
                <div><span><i className="bi bi-person-check" /></span><p><strong>Keep details current</strong><small>Use your latest contact and education information.</small></p></div>
                <div><span><i className="bi bi-bullseye" /></span><p><strong>Match the opportunity</strong><small>Highlight skills relevant to the role.</small></p></div>
                <div><span><i className="bi bi-file-earmark-text" /></span><p><strong>Use a clear format</strong><small>Keep your resume concise and easy to scan.</small></p></div>
              </div>
              <button type="button" className="student-resume-guide-action" onClick={() => setActiveTab('build')}>Improve with Resume Builder <i className="bi bi-arrow-right" /></button>
            </SectionCard>
          </div>
        </div>
      ) : null}

      {activeTab === 'build' && !isStaff ? (
        builderLoading ? (
          <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
        ) : (
          <BuilderSectionEditor
            profile={builderProfile}
            setProfile={setBuilderProfile}
            builderTemplates={builderTemplates}
            builderSuggestions={builderSuggestions}
            onAutoFill={handleAutoFill}
            onFormatAts={handleFormatAts}
            onSave={saveBuilderProfile}
            saving={builderSaving}
          />
        )
      ) : null}

      {(activeTab === 'my' || activeTab === 'all') ? (
        <div className="row g-4">
          {activeTab === 'my' && !isStaff ? (
            <div className="col-12">
              <SectionCard className="student-resume-profile-card" title="Resume Builder Profile" subtitle="Your structured profile is ready to edit anytime.">
                <div className="row g-3 align-items-center">
                  <div className="col-md-8">
                    <div className="fw-semibold">{builderProfile.profileTitle || 'Primary Resume'}</div>
                    <div className="text-muted small mt-1">
                      {builderProfile.resumeType === 'experienced' ? 'Experienced resume flow' : 'Fresher resume flow'} | Template: {builderTemplates.find((item) => item.key === builderProfile.templateKey)?.label || builderProfile.templateKey}
                    </div>
                    <div className="text-muted small mt-1">
                      Skills: {normalizeTagList(builderProfile.technicalSkills).length} | Certifications: {normalizeTagList(builderProfile.certifications).length}
                    </div>
                  </div>
                  <div className="col-md-4 d-flex justify-content-md-end">
                    <button type="button" className="btn btn-outline-primary" onClick={() => setActiveTab('build')}>Open Builder <i className="bi bi-arrow-right ms-1" /></button>
                  </div>
                </div>
              </SectionCard>
            </div>
          ) : null}

          <div className="col-12">
            <SectionCard
              title={activeTab === 'all' ? `All Resume Files (${allTotal})` : 'Uploaded Resume Files'}
              subtitle={activeTab === 'all' ? 'Staff can review student uploads and update placement status.' : 'Uploaded resumes are shown here along with approval status and default selection.'}
              action={activeTab === 'all' ? (
                <div className="d-flex gap-2 flex-wrap">
                  <select className="form-select form-select-sm" style={{ width: 160 }} value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setAllPage(1); }}>
                    <option value="">All Statuses</option>
                    {Object.entries(STATUS_META).map(([key, meta]) => <option key={key} value={key}>{meta.label}</option>)}
                  </select>
                  <select className="form-select form-select-sm" style={{ width: 180 }} value={filterPlace} onChange={(e) => { setFilterPlace(e.target.value); setAllPage(1); }}>
                    <option value="">All Placement</option>
                    {PLACEMENT_STAGES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => fetchAll()}>Refresh</button>
                </div>
              ) : null}
            >
              {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
              ) : docs.length ? (
                <>
                  {docs.map((doc) => (
                    <ResumeCard key={doc._id} doc={doc} isStaff={isStaff} canApprove={canApprove} onSetDefault={handleSetDefault} onDownload={handleDownload} onDelete={handleDelete} onPlacement={setPlacementDoc} onApprove={handleApprove} />
                  ))}
                  {activeTab === 'all' && allTotal > 20 ? (
                    <nav className="mt-3 d-flex justify-content-center">
                      <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${allPage <= 1 ? 'disabled' : ''}`}><button type="button" className="page-link" onClick={() => setAllPage((prev) => Math.max(1, prev - 1))}>Prev</button></li>
                        <li className="page-item active"><span className="page-link">{allPage}</span></li>
                        <li className={`page-item ${allDocs.length < 20 ? 'disabled' : ''}`}><button type="button" className="page-link" onClick={() => setAllPage((prev) => prev + 1)}>Next</button></li>
                      </ul>
                    </nav>
                  ) : null}
                </>
              ) : (
                <div className="text-center text-muted py-5">No resumes found.</div>
              )}
            </SectionCard>
          </div>
        </div>
      ) : null}

      {activeTab === 'jobs' ? (
        jobsLoading ? (
          <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
        ) : (
          <JobBoard jobs={jobs} isAdmin={isStaff} filters={jobFilters} setFilters={setJobFilters} onCreate={openCreateJob} onEdit={openEditJob} onDelete={deleteJob} onToggleActive={toggleJobActive} />
        )
      ) : null}

      <PlacementDialog show={!!placementDoc} resume={placementDoc} onClose={() => setPlacementDoc(null)} onSaved={() => { fetchAll(); showAlert('Placement updated.'); }} />
      <JobEditorModal show={showJobModal} form={jobForm} setForm={setJobForm} editingJob={editingJob} onClose={() => setShowJobModal(false)} onSave={saveJob} saving={jobSaving} />
    </div>
  );
}
